import React from 'react';

import { Spinner } from 'components';
import { useOutletContext } from 'hooks/useOutletContext';
import { type Role, RoleList, User, UserPermissions } from 'models/user';
import { Navigate, Outlet } from 'react-router-dom';

type Permission = keyof UserPermissions;

type RequiredPermissions = (Permission | Partial<Record<Role, Permission>>)[];

type PrivateRouteProps = {
  availableLevel?: Role;
  eligibleRoles?: Role[];
  requiredPermissions?: RequiredPermissions;
};

const hasRequiredPermissions = (user: User, required?: RequiredPermissions): boolean => {
  if (!required || required.length === 0) return true;
  if (user.role === 'ADMIN') return true;

  const userPermissions = user.permissions;

  if (!userPermissions) return false;

  return required.every((permission) => {
    if (typeof permission === 'string') {
      return userPermissions[permission] === true;
    }

    const permissionForRole = permission[user.role];
    if (!permissionForRole) return true;

    return Boolean(userPermissions[permissionForRole]);
  });
};

const isRoleAllowed = (userRole: Role, props: PrivateRouteProps): boolean => {
  if (userRole === 'ADMIN') return true;

  if (props.eligibleRoles) {
    return props.eligibleRoles.includes(userRole);
  }

  if (props.availableLevel) {
    const myAccessValue = RoleList.indexOf(userRole);
    const routeAccessValue = RoleList.indexOf(props.availableLevel);
    return routeAccessValue >= myAccessValue;
  }

  return true;
};

const PrivateRoute = (props: PrivateRouteProps) => {
  const user = useOutletContext();
  const isAdmin = user.role === 'ADMIN';

  const roleAllowed = isRoleAllowed(user.role, props);
  const isPermissionAllowed = hasRequiredPermissions(user, props.requiredPermissions) || isAdmin;

  if (!isAdmin && (!roleAllowed || !isPermissionAllowed)) {
    return <Navigate to={'/me'} />;
  }

  return (
    <React.Suspense fallback={<Spinner />}>
      <Outlet context={user} />
    </React.Suspense>
  );
};

export default PrivateRoute;
